import { Link } from "wouter";
import { GlassCard, PillTag, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import ChallengeProgressRing from "@/components/mnf/ChallengeProgressRing";
import FoundingMemberRibbon from "@/components/mnf/FoundingMemberRibbon";
import {
  queries,
  useStoreQuery,
  type Challenge,
} from "@/lib/store";

/**
 * ChallengeCard
 *
 * Compact challenge card used on Today and Sisters. Shows the title,
 * scripture anchor, the sister's progress ring (or join CTA if she has
 * not joined), participant count (NOT a leaderboard), and a soft tap to
 * the detail page. Pinned variant prefixes "Founding member" ribbon
 * styling for the launch challenge.
 */
export default function ChallengeCard({
  challenge,
  userId,
  variant = "today",
}: {
  challenge: Challenge;
  userId: string;
  variant?: "today" | "list";
}) {
  const progress = useStoreQuery(() => queries.challengeProgress(challenge.id, userId));
  const participantCount = useStoreQuery(() => queries.challengeParticipantCount(challenge.id));
  const foundingCount = useStoreQuery(() => queries.foundingMemberCount());

  if (!progress) return null;

  const tone = challenge.tone;
  const palette = palettes[tone];
  const isLaunch = challenge.isLaunchChallenge;
  const isJoined = !!progress.participant;
  const isFounding = !!progress.participant?.isFoundingMember;
  const daysRemainingLabel =
    progress.daysRemaining === 0
      ? "Closes today"
      : `${progress.daysRemaining} ${progress.daysRemaining === 1 ? "day" : "days"} left`;

  const targetLabel = `${challenge.threshold.targetDays} of ${progress.windowDays} days`;
  const linkHref = `/together/${challenge.slug}`;

  return (
    <Link href={linkHref} className="no-underline block" data-testid={`link-challenge-${challenge.slug}`}>
      <GlassCard
        padding={0}
        className="mnf-fade-up overflow-hidden"
        style={{
          border: `1px solid ${palette.border}`,
        }}
        data-testid={`card-challenge-${challenge.slug}`}
      >
        {/* Hero band */}
        <div
          style={{
            background: palette.heroBg,
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 26 }} aria-hidden>{challenge.hero.primary}</span>
          {challenge.hero.secondary && (
            <span style={{ fontSize: 18, opacity: 0.85 }} aria-hidden>{challenge.hero.secondary}</span>
          )}
          <div className="flex-1 min-w-0">
            <p
              className="mnf-eyebrow"
              style={{ color: palette.eyebrow }}
            >
              {isLaunch ? "Launch challenge" : "Together"}
            </p>
            <p
              className="mnf-serif mt-0.5 truncate"
              style={{ fontSize: 16, fontWeight: 500, color: palette.title, lineHeight: 1.25 }}
            >
              {challenge.title}
            </p>
          </div>
          {isFounding && variant === "today" && (
            <FoundingMemberRibbon count={foundingCount ?? 0} endsLabel={daysRemainingLabel} variant="inline" />
          )}
        </div>

        {/* Body */}
        <div className="px-4 py-4 flex items-center gap-4">
          <ChallengeProgressRing
            daysLogged={progress.daysLogged}
            targetDays={progress.targetDays}
            tone={tone}
            size={isJoined ? 84 : 64}
          />
          <div className="flex-1 min-w-0">
            <p
              className="mnf-serif italic"
              style={{ fontSize: 13.5, lineHeight: 1.45, color: "var(--mnf-fd)" }}
            >
              &ldquo;{challenge.scripture}&rdquo;
            </p>
            <p
              className="mnf-body"
              style={{ fontSize: 11, color: "var(--mnf-tg)", letterSpacing: "0.04em", marginTop: 2 }}
            >
              {challenge.scriptureRef}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <PillTag tone={pillTone(tone)}>
                <Icon name="schedule" size={11} /> {targetLabel}
              </PillTag>
              <span
                className="mnf-body"
                style={{ fontSize: 11, color: "var(--mnf-tg)" }}
                data-testid={`text-participants-${challenge.slug}`}
              >
                {participantCount ?? 0} sisters walking
              </span>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className="px-4 pb-4 pt-0 flex items-center justify-between gap-3"
          style={{ borderTop: "1px solid rgba(30,74,53,0.06)", paddingTop: 12 }}
        >
          <p
            className="mnf-body"
            style={{ fontSize: 11.5, color: palette.eyebrow, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            {progress.completed
              ? "Bearing fruit"
              : isJoined
              ? "Walking with you"
              : progress.canJoin
              ? "Open to join"
              : "Window closed"}
          </p>
          <span className="flex items-center gap-1" style={{ color: palette.eyebrow, fontSize: 12, fontWeight: 700 }}>
            {isJoined ? "Open" : progress.canJoin ? "Join" : "View"}
            <Icon name="arrow_forward" size={14} />
          </span>
        </div>
      </GlassCard>
    </Link>
  );
}

const palettes: Record<
  "forest" | "coral" | "gold" | "purple" | "fiber",
  { heroBg: string; eyebrow: string; title: string; border: string }
> = {
  gold: {
    heroBg: "linear-gradient(135deg, rgba(160,120,32,0.16) 0%, rgba(245,240,232,0.72) 100%)",
    eyebrow: "var(--mnf-gold)",
    title: "var(--mnf-fd)",
    border: "rgba(160,120,32,0.22)",
  },
  forest: {
    heroBg: "linear-gradient(135deg, rgba(30,74,53,0.10) 0%, rgba(245,240,232,0.72) 100%)",
    eyebrow: "var(--mnf-f)",
    title: "var(--mnf-fd)",
    border: "rgba(30,74,53,0.16)",
  },
  coral: {
    heroBg: "linear-gradient(135deg, rgba(192,120,74,0.14) 0%, rgba(245,240,232,0.72) 100%)",
    eyebrow: "var(--mnf-t)",
    title: "var(--mnf-fd)",
    border: "rgba(192,120,74,0.22)",
  },
  purple: {
    heroBg: "linear-gradient(135deg, rgba(127,119,221,0.12) 0%, rgba(245,240,232,0.72) 100%)",
    eyebrow: "var(--mnf-purple)",
    title: "var(--mnf-fd)",
    border: "rgba(127,119,221,0.20)",
  },
  fiber: {
    heroBg: "linear-gradient(135deg, rgba(168,212,184,0.30) 0%, rgba(245,240,232,0.72) 100%)",
    eyebrow: "#2C6248",
    title: "var(--mnf-fd)",
    border: "var(--mnf-fiberBorder)",
  },
};

function pillTone(tone: "forest" | "coral" | "gold" | "purple" | "fiber"): "forest" | "coral" | "gold" | "purple" | "fiber" {
  return tone;
}

// Suppress unused param lint for variant prop reserved for future styles.
ChallengeCard.displayName = "ChallengeCard";
